
import Vue from 'vue'
import DialogTemplate from '@/_components/dialog/template/template.vue'

import { getRequest, patchRequest, postRequest } from '@/plugins/api-axios'
import { deattribute, deserialise, serialise } from 'kitsu-core'
import { TreeViewItem } from '../../ch/contractors.types'
import CompanyTreeX from '@/views/Project/ch/components/companyTreeX.vue'
import UserAutocomplete from '@/views/Project/Settings/Observation/components/userAutocomplete.vue'
import lodash from 'lodash'
export default Vue.extend({
    components: { CompanyTreeX, UserAutocomplete, DialogTemplate },
    data() {
        return {
            loading: true,
            settings: null as any,
            hierarchy: null as any,
            companyList: {
                tree: undefined as undefined | TreeViewItem[],
                includeArchive: false,
                searchTerm: '',
            },
            contractors: [] as any[],
            searchTerm: '',
            worker: null as Worker | null,
        }
    },
    computed: {
        activeContractor() {
            return this.contractors[0] || null
        },

        contractorWithResponsibleLeadersOrUsers() {
            return this.activeContractor
                ? this.settings.contractorsWithResponsibleLeadersOrUsers.data.find(
                      (item) => item.id == this.activeContractor.id
                  )
                : null
        },

        activeContractorDialog: {
            get() {
                return this.$vuetify.breakpoint.mdAndUp
                    ? false
                    : this.activeContractor
                    ? true
                    : false
            },
            set(val) {
                if (!val) {
                    this.contractors = []
                }
            },
        },
    },
    methods: {
        async getCompanyHierarchy() {
            if (!this.currentProjectId) return

            try {
                let { data } = await getRequest(
                    this.getURL({
                        path: `contractorHierarchies/${this.currentProjectId}`,
                        base: process.env.VUE_APP_CONTRACTOR_BASE_URL,
                        params: {
                            include:
                                'nodes,nodes.parent,nodes.company,nodes.companyDetails,nodes.companyDetails.contacts,nodes.companyDetails.documents',
                            skipLinks: '',
                        },
                    }),
                    this.getRequestHeaders()
                )

                this.setHierarchy({ ...data.data })

                let hierarchyData = deserialise(data)
                let ids = {}
                let companyDetails = {}
                let tree: any[] = []
                hierarchyData.data.nodes.data.forEach((node) => {
                    ids[node.id] = { ...node, children: [] }
                    if (node.companyDetails?.data?.id) {
                        companyDetails[node.companyDetails.data.id] = node.id
                    }
                })
                hierarchyData.data.nodes.data.forEach((node) => {
                    const addingNode = ids[node.id]
                    addingNode._isMatch = !addingNode.isArchived
                    if (!node.parent.data) {
                        tree.push(addingNode)
                    } else {
                        ids[node.parent.data.id].children.push(addingNode)
                        ids[node.parent.data.id].children.sort((a, b) => a.index - b.index)
                    }
                })

                this.companyList.tree = tree
            } catch (e) {
                //
            }
        },

        setHierarchy(data: any) {
            if (data) this.hierarchy = deattribute(data)
        },

        async loadProject() {
            let res = await getRequest(
                `${process.env.VUE_APP_OBSERVATIONS_BASE_URL}/projects/${this.currentProjectId}`,
                { 'Content-Type': 'application/vnd.api+json' },
                {
                    include:
                        'settings.defaultResponsibleLeader.user,settings.defaultResponsibleUser.user,settings.contractorsWithResponsibleLeadersOrUsers.defaultResponsibleLeader.user,settings.contractorsWithResponsibleLeadersOrUsers.defaultResponsibleUser.user',
                }
            )
            const project = deserialise(res.data).data
            this.settings = project.settings.data
        },

        async updateResponsible(attr: string, user) {
            let body = serialise('projectSettings', {
                id: this.settings.id,
                type: this.settings.type,
                [attr]: {
                    data: user,
                },
            })

            let res = await patchRequest(
                `${process.env.VUE_APP_OBSERVATIONS_BASE_URL}/projectSettings/${this.settings.id}`,
                { 'Content-Type': 'application/vnd.api+json' },
                body.data,
                {
                    include:
                        'defaultResponsibleLeader.user,defaultResponsibleUser.user,contractorsWithResponsibleLeadersOrUsers.defaultResponsibleLeader.user,contractorsWithResponsibleLeadersOrUsers.defaultResponsibleUser.user',
                }
            )

            this.settings = deserialise(res.data).data
        },

        async setContractorResponsibleLeader(projectUser) {
            let res = await postRequest(
                `${process.env.VUE_APP_OBSERVATIONS_BASE_URL}/projectSettings/${this.settings.id}/setContractorResponsibleLeader`,
                { 'Content-Type': 'application/vnd.api+json' },
                null,
                {
                    include:
                        'defaultResponsibleLeader.user,defaultResponsibleUser.user,contractorsWithResponsibleLeadersOrUsers.defaultResponsibleLeader.user,contractorsWithResponsibleLeadersOrUsers.defaultResponsibleUser.user',
                    contractorId: this.activeContractor.id,
                    projectUserId: projectUser ? projectUser.id : null,
                }
            )

            this.settings = deserialise(res.data).data
        },

        async setContractorResponsibleUser(projectUser) {
            let res = await postRequest(
                `${process.env.VUE_APP_OBSERVATIONS_BASE_URL}/projectSettings/${this.settings.id}/setContractorResponsibleUser`,
                { 'Content-Type': 'application/vnd.api+json' },
                null,
                {
                    include:
                        'defaultResponsibleLeader.user,defaultResponsibleUser.user,contractorsWithResponsibleLeadersOrUsers.defaultResponsibleLeader.user,contractorsWithResponsibleLeadersOrUsers.defaultResponsibleUser.user',
                    contractorId: this.activeContractor.id,
                    projectUserId: projectUser ? projectUser.id : null,
                }
            )

            this.settings = deserialise(res.data).data
        },

        filterContractors() {
            this.worker?.postMessage({
                type: 'filterContractor',
                payload: {
                    tree: this.companyList.tree,
                    searchTerm: this.companyList.searchTerm,
                },
            })
        },

        handleContractorSearchTermChange: lodash.debounce(async function (this) {
            this.filterContractors()
        }, 500),
    },
    watch: {
        currentProjectId: {
            immediate: true,
            async handler() {
                this.loading = true
                await Promise.all([this.loadProject(), this.getCompanyHierarchy()])
                this.loading = false
            },
        },
    },

    mounted() {
        if (!window.Worker) return

        this.worker = new Worker(`/contractor-worker.js?v=${process.env.VUE_APP_VERSION}`)
        this.worker.onmessage = (e) => {
            if (!Array.isArray(e.data.payload)) return

            if (e.data.type === 'contractorFilteredResponse') {
                this.companyList.tree = [...e.data.payload]
            }
        }
    },
    beforeDestroy() {
        this.worker?.terminate()
    },
})
